import {Card, Box, Typography, Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../../../../i18n/translations';
import {Redirect, useHistory, useParams, useRouteMatch} from 'react-router-dom';
import GenericHeader from '../../../../../../../components/generic-header/generic-header';
import {UserCaregiverForm} from '../../../../../../../components/user-form/user-caregiver-form';
import {CaregiverPathExtensions} from '../../../../../../../components/caregiver-routes/caregiver-routes';
import InviteProfessionalCaregiverProfessionalCaregiverOfficeScreen from '../../invite-professional-caregiver/invite-professional-caregiver.professional-caregiver.screen';
import {useAuth} from '../../../../../../../contexts/auth/context';
import {useOffice} from '../../../../../../../contexts/office/context';
import {useObserveViewport} from '../../../../../../../hooks';
import {AffiliationType} from '../../../../../../../backend/enums';
import {CaregiverMode, ScreenPaths, UserFormType} from '../../../../../../../enums';
import {useEffect, useMemo, useState} from 'react';
import {UsersRepository} from '../../../../../../../repositories/users';
import {EntitiesRepository} from '../../../../../../../repositories/entities';
import {useSnackBar} from '../../../../../../../contexts/snack-bar/context';
import {RolePermissionType} from '../../../../../../../backend/enums/role-permission-type';
import {LoadingContent} from '../../../../../../../components/loading-content/loading-content';

export default function ProfessionalCaregiverProfileProfessionalCaregiverOfficeScreen({
  caregiverMode,
}: {
  caregiverMode: CaregiverMode.SystemAdministrator | CaregiverMode.Professional;
}) {
  const {hasReadPermission, hasWritePermission, state} = useAuth();
  const officeContext = useOffice();
  const snackBarContext = useSnackBar();
  const {isRunningMobile} = useObserveViewport();
  let {id, entityId}: {id?: string; entityId?: string} = useParams();

  if (id?.toLowerCase() === 'invite') {
    id = undefined;
  }

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url} = useRouteMatch();
  let history = useHistory();
  const {t} = useTranslation();
  const [isLoading, setIsLoading] = useState(true);

  const [readPermission, writePermission, writeTeamPermissionsPermission] = useMemo(
    () => [
      hasReadPermission(RolePermissionType.Team_Professionals_Profile),
      hasWritePermission(RolePermissionType.Team_Professionals_Profile),
      hasWritePermission(RolePermissionType.Team_Permissions),
    ],
    [hasWritePermission, hasReadPermission]
  );
  const userBreadcrumbName =
    officeContext.state.userForm?.user?.displayName || officeContext.state.userForm?.user?.name;

  const userFormType = (() => {
    if (id === state.session?.id) {
      return UserFormType.EditCurrentUser;
    }
    if (writePermission) return UserFormType.CreateEditRemoveSenior_ProfessionalCaregiver;
    if (readPermission)
      return UserFormType.ReadOnlyCaregiver_ProfessionalCaregiverManagerAndOperator;

    return undefined;
  })();

  // Validate and get the user
  useEffect(() => {
    const getUser = async () => {
      if (id) {
        try {
          if (caregiverMode === CaregiverMode.SystemAdministrator) {
            // Get user
            const userDetails = await UsersRepository.instance.getById(id);

            if (userDetails) {
              // Set user for edit/view
              officeContext.setUserForm(userDetails);
              setIsLoading(false);
            } else {
              // Error
              throw new Error('Empty user.');
            }
          } else {
            // Check entity id
            if (!entityId) {
              // Error
              throw new Error('Empty entity id.');
            }

            // Get user and affiliation
            const userAffiliationDetails = await EntitiesRepository.instance.getAffiliation(
              entityId,
              id
            );
            if (userAffiliationDetails) {
              // Set user for edit/view
              officeContext.setUserForm(
                userAffiliationDetails.user,
                userAffiliationDetails.affiliation,
                userAffiliationDetails.affiliationQuickAccess
              );
              setIsLoading(false);
            } else {
              // Error
              throw new Error('Empty user.');
            }
          }
        } catch (error) {
          console.debug('🔴 Could not get user.', error);

          // Show toast error
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.caregiver_tabs.errors.caregiver_does_not_exist_error),
          });

          history.goBack();
        }
      } else {
        if (caregiverMode === CaregiverMode.SystemAdministrator) {
          console.debug('🔴 Could not get user.');

          // Show toast error
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.caregiver_tabs.errors.caregiver_does_not_exist_error),
          });

          history.goBack();
        }
        // Invite screen
      }
    };

    // Get user
    getUser();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Only professional caregivers that are managers or administrators can send invites to other
  // professional caregivers at the moment. All other users can't use this button.
  return (
    <>
      {id ? (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
          <GenericHeader
            titleCrumbs={
              userBreadcrumbName
                ? isRunningMobile
                  ? [
                      {
                        label: t(
                          translations.office.professional_caregiver.profile.generic_header
                            .breadcrumbs.profile
                        ),
                        link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}/${id}`,
                      },
                    ]
                  : [
                      {
                        label: t(
                          translations.office.professional_caregiver.profile.generic_header
                            .breadcrumbs.team
                        ),
                        link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}`,
                      },
                      {
                        label: t(
                          translations.office.professional_caregiver.profile.generic_header
                            .breadcrumbs.professionals
                        ),
                        link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}`,
                      },
                      {
                        label: userBreadcrumbName,
                        link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}/${id}/${CaregiverPathExtensions.Profile}`,
                      },
                      {
                        label: t(
                          translations.office.professional_caregiver.profile.generic_header
                            .breadcrumbs.profile
                        ),
                        link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}/${id}/${CaregiverPathExtensions.Profile}`,
                      },
                    ]
                : undefined
            }
            isLoading={officeContext.state.userFormLoadingState}
            leftColumn={
              <Typography
                sx={{
                  fontSize: '18px',
                  fontWeight: 400,
                  lineHeight: '18px',
                  color: 'white',
                  maxWidth: {xs: '250px', sm: '100%'},
                }}
                noWrap
              >
                {t(translations.office.professional_caregiver.profile.generic_header.subtitle.read)}
              </Typography>
            }
          />
          <Card
            sx={{
              padding: {xs: '40px 10px 51px 10px', md: '40px 66px 51px 66px'},
              minHeight: `calc(100vh - 280px)`,
              borderRadius: '10px',
            }}
            elevation={0}
          >
            {userFormType && !isLoading ? (
              <UserCaregiverForm
                userFormType={userFormType}
                canEditRole={writeTeamPermissionsPermission}
                redirectUrl={`${url.replace(`/${id}`, '')}`}
                entityId={entityId}
              />
            ) : (
              <Stack
                height={`calc(100vh - 108px - (2 * 20px) - 70px - 40px)`}
                sx={{justifyContent: 'center', alignItems: 'center'}}
              >
                <LoadingContent />
              </Stack>
            )}
          </Card>
        </Box>
      ) : state.session?.selectedAffiliation?.type === AffiliationType.Administrator ||
        state.session?.selectedAffiliation?.type === AffiliationType.Manager ? (
        <InviteProfessionalCaregiverProfessionalCaregiverOfficeScreen />
      ) : (
        // Redirect to the professional caregivers listing screen
        <Redirect to={`${url.replace('/invite', '')}`} />
      )}
    </>
  );
}
